import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  UserPlus, Phone, Mail, MapPin, Plus, Search, CheckCircle2, Circle, 
  FileText, Trash2, Edit3, Save, X, Clock, ArrowRight, User, Check, Calendar
} from 'lucide-react';
import Button from './ui/Button';

const Prospects = ({ user, activeCompanyId, onCreateQuote }) => {
  const [prospects, setProspects] = useState([]);
  const [clients, setClients] = useState([]);
  const [view, setView] = useState('list'); // 'list', 'new', 'detail'
  const [activeProspect, setActiveProspect] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user || !activeCompanyId) return;
    
    const q = query(
      collection(db, 'companies', activeCompanyId, 'prospects'), 
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setProspects(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Charger la liste des clients
    const clientsUnsub = onSnapshot(
      collection(db, 'companies', activeCompanyId, 'clients'),
      (snapshot) => {
        setClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    );

    return () => {
      unsubscribe();
      clientsUnsub();
    };
  }, [user, activeCompanyId]);

  const filteredProspects = prospects.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 animate-page pb-24">
      {view === 'list' && (
        <ProspectList 
          prospects={filteredProspects}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onNew={() => setView('new')}
          onSelect={(prospect) => { setActiveProspect(prospect); setView('detail'); }}
        />
      )}
      
      {view === 'new' && (
        <NewProspectForm 
          activeCompanyId={activeCompanyId}
          clients={clients}
          onCancel={() => setView('list')}
          onSuccess={() => setView('list')}
        />
      )}
      
      {view === 'detail' && activeProspect && (
        <ProspectDetail 
          prospect={activeProspect}
          activeCompanyId={activeCompanyId}
          clients={clients}
          onBack={() => { setActiveProspect(null); setView('list'); }}
          onCreateQuote={onCreateQuote}
        />
      )}
    </div>
  );
};

// --- LISTE DES PROSPECTS ---
const ProspectList = ({ prospects, searchTerm, setSearchTerm, onNew, onSelect }) => {
  const stats = {
    total: prospects.length,
    contacted: prospects.filter(p => p.status === 'contacted').length,
    quoted: prospects.filter(p => p.status === 'quoted').length,
  };

  return (
    <div className="animate-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <UserPlus className="text-purple-600" size={32}/>
            Prospects
          </h1>
          <p className="text-slate-500 text-sm mt-1">Gérez vos futurs clients et leurs besoins</p>
        </div>
        <Button variant="primary" onClick={onNew} icon={Plus}>
          Nouveau Prospect
        </Button>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          <div className="text-xs text-blue-600 font-medium">Total</div>
        </div>
        <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
          <div className="text-2xl font-bold text-orange-600">{stats.contacted}</div>
          <div className="text-xs text-orange-600 font-medium">Contactés</div>
        </div>
        <div className="bg-green-50 p-4 rounded-xl border border-green-100">
          <div className="text-2xl font-bold text-green-600">{stats.quoted}</div>
          <div className="text-xs text-green-600 font-medium">Devis créés</div>
        </div>
      </div>

      {/* Recherche */}
      <div className="mb-6 relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20}/>
        <input 
          type="text"
          placeholder="Rechercher un prospect..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      {/* Liste */}
      <div className="space-y-3">
        {prospects.length === 0 && (
          <div className="text-center py-16 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
            <UserPlus size={48} className="text-slate-300 mx-auto mb-4"/>
            <p className="text-slate-500 font-medium mb-2">Aucun prospect</p>
            <p className="text-sm text-slate-400 mb-4">Ajoutez vos futurs clients et leurs besoins</p>
            <Button variant="primary" onClick={onNew} icon={Plus}>Créer le premier</Button>
          </div>
        )}

        {prospects.map(prospect => (
          <div 
            key={prospect.id}
            onClick={() => onSelect(prospect)}
            className="bg-white p-4 rounded-xl border border-slate-200 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold">
                    {prospect.name?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-800 group-hover:text-purple-600 transition">
                      {prospect.name || 'Sans nom'}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                      {prospect.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={12}/> {prospect.phone}
                        </span>
                      )}
                      {prospect.address && (
                        <span className="flex items-center gap-1">
                          <MapPin size={12}/> {prospect.address}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Travaux à faire */}
                {prospect.tasks && prospect.tasks.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {prospect.tasks.slice(0, 3).map((task, idx) => (
                      <span 
                        key={idx}
                        className={`text-xs px-2 py-1 rounded-full ${
                          task.done 
                            ? 'bg-green-100 text-green-700 line-through' 
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {task.title}
                      </span>
                    ))}
                    {prospect.tasks.length > 3 && (
                      <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-500">
                        +{prospect.tasks.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Status badge */}
              <div className="flex items-center gap-2">
                {prospect.quoteId && (
                  <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">
                    Devis créé
                  </span>
                )}
                {!prospect.quoteId && prospect.status === 'contacted' && (
                  <span className="bg-orange-100 text-orange-700 text-xs font-bold px-3 py-1 rounded-full">
                    Contacté
                  </span>
                )}
                {!prospect.quoteId && prospect.status === 'new' && (
                  <span className="bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full">
                    Nouveau
                  </span>
                )}
                <ArrowRight size={18} className="text-slate-300 group-hover:text-purple-500 transition"/>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- FORMULAIRE NOUVEAU PROSPECT ---
const NewProspectForm = ({ activeCompanyId, clients, onCancel, onSuccess }) => {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    notes: '',
    detailedDescription: '',
    status: 'new',
    clientId: '', // Lien vers client existant
    linkedClientName: '' // Nom du client lié (pour affichage)
  });

  // Quand on sélectionne un client existant, pré-remplir les infos
  const handleSelectClient = (clientId) => {
    if (!clientId) {
      setForm(prev => ({ ...prev, clientId: '', linkedClientName: '', name: '', phone: '', email: '' }));
      return;
    }
    
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setForm(prev => ({
        ...prev,
        clientId: client.id,
        linkedClientName: client.name,
        name: client.name,
        phone: client.phone || '',
        email: client.email || ''
      }));
    }
  };

  const handleSubmit = async () => {
    if (!form.name) {
      alert('Le nom est obligatoire');
      return;
    }

    try {
      // Si le prospect n'est pas lié à un client existant, créer un nouveau client
      let clientId = form.clientId;
      if (!clientId && form.name) {
        const newClientRef = await addDoc(collection(db, 'companies', activeCompanyId, 'clients'), {
          id: Date.now(),
          companyId: activeCompanyId,
          name: form.name,
          email: form.email || '',
          phone: form.phone || '',
          address: form.address || '',
          city: form.city || '',
          createdAt: serverTimestamp()
        });
        clientId = newClientRef.id;
      }

      await addDoc(collection(db, 'companies', activeCompanyId, 'prospects'), {
        ...form,
        clientId: clientId || '',
        tasks: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      onSuccess();
    } catch (error) {
      console.error('Erreur création prospect:', error);
      alert('Erreur lors de la création');
    }
  };

  return (
    <div className="animate-page">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <UserPlus className="text-purple-600" size={28}/>
          Nouveau Prospect
        </h2>

        <div className="space-y-4">
          {/* Sélection client existant */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
              <User size={16} className="text-blue-600"/>
              Client existant (optionnel)
            </label>
            
            {clients.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Aucun client enregistré. Créez d'abord un client dans l'onglet Clients.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => handleSelectClient('')}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition ${
                    !form.clientId 
                      ? 'border-blue-500 bg-blue-100 text-blue-900 font-medium' 
                      : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'
                  }`}
                >
                  ➕ Nouveau client
                </button>
                {clients.map(client => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => handleSelectClient(client.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition ${
                      form.clientId === client.id 
                        ? 'border-blue-500 bg-blue-100 text-blue-900 font-medium' 
                        : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-medium">{client.name}</div>
                    {client.phone && <div className="text-xs text-slate-500 mt-1">{client.phone}</div>}
                  </button>
                ))}
              </div>
            )}
            
            {form.clientId && (
              <p className="text-xs text-blue-600 mt-3 flex items-center gap-1">
                <Check size={12}/> Coordonnées pré-remplies. Ajoutez l'adresse du bien ci-dessous.
              </p>
            )}
          </div>

          {/* Nom */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Nom du client *
            </label>
            <input 
              type="text"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              disabled={!!form.clientId}
              placeholder="M. Dupont"
              className={`w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500 ${form.clientId ? 'bg-slate-100 cursor-not-allowed' : 'bg-slate-50'}`}
              autoFocus={!form.clientId}
            />
          </div>

          {/* Téléphone */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              <Phone size={14} className="inline mr-1"/> Téléphone
            </label>
            <input 
              type="tel"
              disabled={!!form.clientId}
              value={form.phone}
              onChange={(e) => setForm({...form, phone: e.target.value})}
              placeholder="06 12 34 56 78"
              className={`w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500 ${form.clientId ? 'bg-slate-100 cursor-not-allowed' : 'bg-slate-50'}`}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              <Mail size={14} className="inline mr-1"/> Email
            </label>
            <input 
              type="email"
              disabled={!!form.clientId}
              value={form.email}
              onChange={(e) => setForm({...form, email: e.target.value})}
              placeholder="client@email.com"
              className={`w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500 ${form.clientId ? 'bg-slate-100 cursor-not-allowed' : 'bg-slate-50'}`}
            />
          </div>

          {/* Adresse */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              <MapPin size={14} className="inline mr-1"/> Adresse du chantier
            </label>
            <input 
              type="text"
              value={form.address}
              onChange={(e) => setForm({...form, address: e.target.value})}
              placeholder="12 Rue des Artisans"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Ville et Code postal */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">
                Code postal
              </label>
              <input 
                type="text"
                value={form.postalCode}
                onChange={(e) => setForm({...form, postalCode: e.target.value})}
                placeholder="75001"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">
                Ville
              </label>
              <input 
                type="text"
                value={form.city}
                onChange={(e) => setForm({...form, city: e.target.value})}
                placeholder="Paris"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          {/* Notes rapides */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Notes rapides
            </label>
            <textarea 
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              placeholder="Notes courtes (budget estimé, délai souhaité...)"
              rows={2}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500 resize-none"
            />
          </div>

          {/* Description détaillée des travaux */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              📝 Description détaillée des travaux
            </label>
            <textarea 
              value={form.detailedDescription}
              onChange={(e) => setForm({...form, detailedDescription: e.target.value})}
              placeholder="Décrivez en détail les travaux demandés par le client...\n\nExemple :\n- Refaire la salle de bain (6m²)\n- Carrelage au sol + faïence murale\n- Pose d'un receveur de douche 90x90\n- Installation meuble vasque\n- Peinture plafond"
              rows={8}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500 resize-none font-mono text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-100">
          <Button variant="secondary" onClick={onCancel}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!form.name}>
            Créer le prospect
          </Button>
        </div>
      </div>
    </div>
  );
};

// --- DÉTAIL PROSPECT ---
const ProspectDetail = ({ prospect, activeCompanyId, clients, onBack, onCreateQuote }) => {
  const [data, setData] = useState(prospect);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(prospect);
  const [newTask, setNewTask] = useState('');
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);
  const quoteCreationInProgress = React.useRef(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'companies', activeCompanyId, 'prospects', prospect.id),
      (doc) => {
        if (doc.exists()) {
          setData({ id: doc.id, ...doc.data() });
          setEditForm({ id: doc.id, ...doc.data() });
        }
      }
    );
    return () => unsubscribe();
  }, [prospect.id, activeCompanyId]);

  const updateProspect = async (updates) => {
    try {
      await updateDoc(doc(db, 'companies', activeCompanyId, 'prospects', prospect.id), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Erreur mise à jour:', error);
    }
  };

  const handleSaveEdit = async () => {
    await updateProspect(editForm);
    setIsEditing(false);
  };

  const handleAddTask = async () => {
    if (!newTask.trim()) return;
    
    const task = {
      id: Date.now(),
      title: newTask,
      createdAt: new Date().toISOString()
    };

    await updateProspect({
      tasks: [...(data.tasks || []), task]
    });
    
    setNewTask('');
  };

  const handleDeleteTask = async (taskId) => {
    const updatedTasks = (data.tasks || []).filter(t => t.id !== taskId);
    await updateProspect({ tasks: updatedTasks });
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer le prospect "${data.name}" ?`)) return;
    
    try {
      await deleteDoc(doc(db, 'companies', activeCompanyId, 'prospects', prospect.id));
      onBack();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleCreateQuote = async (e) => {
    // Empêcher la propagation et le comportement par défaut
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (isCreatingQuote || quoteCreationInProgress.current) {
      console.log('⚠️ Création déjà en cours, ignoré');
      return;
    }
    
    // Double protection immédiate
    quoteCreationInProgress.current = true;
    setIsCreatingQuote(true);
    
    console.log('🚀 Début création devis - protection activée');
    
    // Petit délai pour s'assurer que le state est mis à jour
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      // Passer les infos du prospect au composant de création de devis
      const fullAddress = [
        data.address,
        data.postalCode,
        data.city
      ].filter(Boolean).join(', ');
      
      const quoteData = {
        prospectId: data.id,
        clientName: data.name,
        clientEmail: data.email || '',
        clientPhone: data.phone || '',
        clientAddress: fullAddress || data.address || '',
        clientCity: data.city || '',
        clientPostalCode: data.postalCode || '',
        tasks: data.tasks || [],
        notes: data.notes || '',
        detailedDescription: data.detailedDescription || ''
      };
      
      console.log('🚀 Données devis préparées:', quoteData);
      
      // Marquer le prospect comme ayant un devis en cours
      await updateProspect({ 
        status: 'quoted',
        quoteCreatedAt: new Date().toISOString()
      });
      
      console.log('✅ Prospect mis à jour, appel onCreateQuote');
      
      // Appeler la fonction parent pour créer le devis
      onCreateQuote(quoteData);
      
      // Ne pas réinitialiser car on change de page
    } catch (error) {
      console.error('❌ Erreur création devis:', error);
      quoteCreationInProgress.current = false;
      setIsCreatingQuote(false);
    }
  };

  const totalTasks = (data.tasks || []).length;

  return (
    <div className="animate-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 to-purple-700 text-white p-6 rounded-2xl mb-6 shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -z-10"></div>
        
        <button 
          onClick={onBack}
          className="text-purple-100 hover:text-white flex items-center gap-2 text-sm font-medium mb-4 transition relative z-10"
        >
          <ArrowRight size={16} className="rotate-180"/> Retour aux prospects
        </button>

        {!isEditing ? (
          <>
            <div className="flex justify-between items-start mb-4 relative z-10">
              <div className="flex-1">
                <h1 className="text-3xl font-bold mb-2">{data.name}</h1>
                {data.clientId && data.linkedClientName && (
                  <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-lg mb-2">
                    <User size={14}/>
                    <span className="text-xs font-medium">Client : {data.linkedClientName}</span>
                  </div>
                )}
                <div className="space-y-1 text-purple-100">
                  {data.phone && (
                    <p className="flex items-center gap-2 text-sm">
                      <Phone size={14}/> {data.phone}
                    </p>
                  )}
                  {data.email && (
                    <p className="flex items-center gap-2 text-sm">
                      <Mail size={14}/> {data.email}
                    </p>
                  )}
                  {(data.address || data.city) && (
                    <p className="flex items-center gap-2 text-sm">
                      <MapPin size={14}/> {[data.address, data.postalCode, data.city].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2">
                <button 
                  onClick={() => setIsEditing(true)}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition"
                >
                  <Edit3 size={18}/>
                </button>
                <button 
                  onClick={handleDelete}
                  className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-xl transition"
                >
                  <Trash2 size={18}/>
                </button>
              </div>
            </div>

            {data.notes && (
              <div className="bg-white/10 rounded-xl p-3 text-sm">
                <p className="text-purple-100">{data.notes}</p>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <input 
              value={editForm.name}
              onChange={(e) => setEditForm({...editForm, name: e.target.value})}
              className="w-full px-4 py-2 rounded-xl bg-white/20 border border-white/30 text-white placeholder-purple-200 outline-none"
              placeholder="Nom"
            />
            <input 
              value={editForm.phone}
              onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
              className="w-full px-4 py-2 rounded-xl bg-white/20 border border-white/30 text-white placeholder-purple-200 outline-none"
              placeholder="Téléphone"
            />
            <input 
              value={editForm.email}
              onChange={(e) => setEditForm({...editForm, email: e.target.value})}
              className="w-full px-4 py-2 rounded-xl bg-white/20 border border-white/30 text-white placeholder-purple-200 outline-none"
              placeholder="Email"
            />
            <input 
              value={editForm.address}
              onChange={(e) => setEditForm({...editForm, address: e.target.value})}
              className="w-full px-4 py-2 rounded-xl bg-white/20 border border-white/30 text-white placeholder-purple-200 outline-none"
              placeholder="Adresse"
            />
            <div className="grid grid-cols-2 gap-2">
              <input 
                value={editForm.postalCode || ''}
                onChange={(e) => setEditForm({...editForm, postalCode: e.target.value})}
                className="w-full px-4 py-2 rounded-xl bg-white/20 border border-white/30 text-white placeholder-purple-200 outline-none"
                placeholder="Code postal"
              />
              <input 
                value={editForm.city || ''}
                onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                className="w-full px-4 py-2 rounded-xl bg-white/20 border border-white/30 text-white placeholder-purple-200 outline-none"
                placeholder="Ville"
              />
            </div>
            <textarea 
              value={editForm.notes}
              onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
              className="w-full px-4 py-2 rounded-xl bg-white/20 border border-white/30 text-white placeholder-purple-200 outline-none resize-none"
              placeholder="Notes rapides"
              rows={2}
            />
            <textarea 
              value={editForm.detailedDescription || ''}
              onChange={(e) => setEditForm({...editForm, detailedDescription: e.target.value})}
              className="w-full px-4 py-2 rounded-xl bg-white/20 border border-white/30 text-white placeholder-purple-200 outline-none resize-none font-mono text-sm"
              placeholder="Description détaillée des travaux"
              rows={6}
            />
            <div className="flex justify-end gap-2">
              <button 
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-sm font-medium"
              >
                Annuler
              </button>
              <button 
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-white hover:bg-white/90 text-purple-600 rounded-xl text-sm font-bold flex items-center gap-2"
              >
                <Save size={16}/> Enregistrer
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action rapide : Créer devis */}
      {!data.quoteId && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-slate-800 mb-1 flex items-center gap-2">
                <FileText size={20} className="text-blue-600"/>
                Préparer le devis au bureau
              </h3>
              <p className="text-sm text-slate-600">
                Crée un brouillon avec les infos client + travaux listés.<br/>
                Vous pourrez ajouter les prix et détails tranquillement.
              </p>
            </div>
            <Button variant="primary" onClick={handleCreateQuote} icon={FileText} disabled={isCreatingQuote}>
              {isCreatingQuote ? 'Création...' : 'Préparer le devis'}
            </Button>
          </div>
        </div>
      )}

      {data.quoteId && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6">
          <p className="text-green-700 font-medium flex items-center gap-2">
            <CheckCircle2 size={20}/>
            Devis créé avec succès !
          </p>
        </div>
      )}

      {/* Description détaillée */}
      {data.detailedDescription && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
            📝 Description détaillée des travaux
          </h2>
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 whitespace-pre-wrap font-mono text-sm text-slate-700">
            {data.detailedDescription}
          </div>
        </div>
      )}

      {/* Liste des travaux */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <CheckCircle2 size={24} className="text-purple-600"/>
            Liste des travaux ({totalTasks})
          </h2>
        </div>

        {/* Ajouter une tâche */}
        <div className="flex gap-2 mb-4">
          <input 
            type="text"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddTask()}
            placeholder="Ex: Rénovation salle de bain..."
            className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500"
          />
          <button 
            onClick={handleAddTask}
            className="px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition flex items-center gap-2"
          >
            <Plus size={20}/>
          </button>
        </div>

        {/* Liste */}
        <div className="space-y-2">
          {(data.tasks || []).length === 0 && (
            <p className="text-center text-slate-400 py-8 text-sm">
              Ajoutez les travaux à réaliser pour ce client
            </p>
          )}

          {(data.tasks || []).map(task => (
            <div 
              key={task.id}
              className="flex items-center gap-3 p-3 rounded-xl border bg-white border-slate-200 hover:border-purple-300 hover:shadow-sm transition-all group"
            >
              <span className="flex-1 text-sm font-medium text-slate-700">
                • {task.title}
              </span>
              
              <button 
                onClick={() => handleDeleteTask(task.id)}
                className="p-1 text-slate-300 hover:text-red-500 transition opacity-0 group-hover:opacity-100"
              >
                <Trash2 size={14}/>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Prospects;
